import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ClipboardList } from "lucide-react";
import { pad, wdLabel } from "@/lib/yasamaru";

export function SubmissionDetailDialog({ user, period, days, closedDates, requests, onClose }) {
  if (!user || !period) return null;
  const userReqs = requests.filter((r) => r.user_id === user.user_id);
  const reqByDate = {};
  for (const r of userReqs) reqByDate[r.date] = r;
  const closedSet = new Set(closedDates);
  const dayList = [];
  for (let d = 1; d <= days; d++) {
    const dateStr = `${period.year}-${pad(period.month)}-${pad(d)}`;
    const wd = new Date(period.year, period.month - 1, d).getDay();
    dayList.push({ d, wd, isClosed: closedSet.has(dateStr), r: reqByDate[dateStr] });
  }
  const sym = (t) => t === "available" ? "◎" : t === "either" ? "△" : t === "unavailable" ? "×" : "未";
  const tone = (t) => t === "available" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                    : t === "either" ? "text-amber-700 bg-amber-50 border-amber-200"
                    : t === "unavailable" ? "text-rose-700 bg-rose-50 border-rose-200"
                    : "text-slate-500 bg-slate-50 border-slate-200";
  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="submission-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-blue-600" />{user.display_name || user.name} さんの希望
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-600 mb-2 flex flex-wrap gap-3">
          <span>◎ <b className="text-emerald-700">{user.requested_days}</b>日</span>
          <span>△ <b className="text-amber-700">{user.either_days}</b>日</span>
          <span>× <b className="text-rose-700">{user.unavailable_days}</b>日</span>
        </div>
        <ul className="divide-y divide-slate-100 border border-slate-200 rounded">
          {dayList.map(({ d, wd, isClosed, r }) => (
            <li key={d} className={`px-3 py-1.5 flex items-center justify-between gap-2 text-sm ${isClosed ? "bg-slate-50" : ""}`}>
              <div className="flex items-center gap-2">
                <span className={`font-bold w-9 text-right ${wd === 0 ? "text-red-500" : wd === 6 ? "text-blue-500" : "text-slate-700"}`}>{d}日</span>
                <span className="text-[11px] text-slate-400">({wdLabel(wd)})</span>
                {isClosed && <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">休業</span>}
              </div>
              {isClosed ? (
                <span className="text-[11px] text-slate-300">—</span>
              ) : (
                <div className="flex items-center gap-2">
                  <span className={`text-[11px] px-1.5 py-0.5 rounded border font-semibold ${tone(r?.request_type)}`}>
                    {sym(r?.request_type)} {r?.request_type === "available" ? "入れます" : r?.request_type === "either" ? "必要なら" : r?.request_type === "unavailable" ? "入れません" : "提出"}
                  </span>
                  {r?.start_time && r?.end_time && (
                    <span className="text-[10px] text-slate-500">{r.start_time}〜{r.end_time}</span>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="submission-detail-close">閉じる</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
